'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React, { useEffect, useState, type Ref } from 'react';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import { useAutocomplete } from '@repo/ui/src/hooks/search/useAutocomplete';
import { classNames } from '@repo/ui/src/utils/classNames';

interface SearchAutocompleteProps {
  placeholder?: string;
  searchTerm?: string;
  setSearchTerm: (term: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
}

const SearchAutocomplete = ({
  placeholder,
  searchTerm,
  setSearchTerm,
  inputRef,
  className,
}: SearchAutocompleteProps) => {
  const [value, setValue] = useState<string | null>(searchTerm || null);
  const [inputValue, setInputValue] = useState<string>(value || '');
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const { t } = useTranslation();
  // if not open pass an empty string to bail out of the query
  const { data, isValidating } = useAutocomplete(open ? inputValue : '');
  const options = inputValue ? (data?.hits.map(({ query }) => query) ?? []) : value ? [value] : [];
  const loading = Boolean(inputValue) && isValidating;

  const handleSearchClick = () => {
    setValue(inputValue);
    setSearchTerm(inputValue);
    setOpen(false);
  };

  const handleInput = (value: string) => {
    setOpen(true);
    setActiveIndex(-1);
    setInputValue(value);
  };

  const selectOption = (option: string) => {
    setValue(option);
    setInputValue(option);
    setSearchTerm(option);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0) {
          e.preventDefault();
          selectOption(options[activeIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  useEffect(() => {
    setValue(searchTerm ?? null);
    setInputValue(searchTerm ?? '');
  }, [searchTerm]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSearchClick();
      }}
      className={classNames('relative w-full', className)}
      data-testid="searchAutoComplete"
    >
      <label id="search-label" htmlFor="search-input" className="sr-only">
        {placeholder || 'Search'}
      </label>
      <div id="instructions" className="hidden">
        Begin typing to search, use arrow keys to navigate, Enter to select
      </div>
      <div className="relative w-full">
        <input
          autoComplete="off"
          id="search-input"
          ref={inputRef}
          type="text"
          className={classNames(
            'w-full border-2 border-gray-300 py-4 px-8 focus:outline-none placeholder-gray-500',
            open && options.length > 0
              ? 'rounded-t-3xl'
              : 'rounded-3xl focus:outline-2 focus:ring focus:ring-brand-200',
          )}
          placeholder={placeholder || 'Search'}
          value={inputValue}
          aria-labelledby="search-label"
          aria-haspopup="listbox"
          aria-controls="results"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-activedescendant={activeIndex >= 0 ? `option-${activeIndex}` : undefined}
          aria-describedby="instructions"
          data-testid="searchAutoCompleteInput"
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          onClick={handleSearchClick}
          type="button"
          className="absolute right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
        >
          <SearchIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      {open && options.length > 0 && (
        <ul
          id="results"
          role="listbox"
          className="absolute list-none pl-0 py-2 w-full bg-white border-2 border-t-0 border-gray-300 rounded-b-3xl shadow-lg z-101"
        >
          {loading ? (
            <li className="w-full px-3 py-3 text-gray-500">
              {t('genericContent.loadingLabel', undefined, { default: 'Loading...' })}
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={index}
                id={`option-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={classNames(
                  'w-full px-8 py-3 cursor-pointer hover:bg-gray-100',
                  index === activeIndex && 'bg-gray-100',
                )}
                onMouseDown={() => selectOption(option)}
              >
                {option}
              </li>
            ))
          )}
        </ul>
      )}
    </form>
  );
};

export default SearchAutocomplete;
